import { CavortAuthProvider, type ComponentSlots, setApiBase } from "@cavort/auth";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRootRoute, Outlet } from "@tanstack/react-router";
import type { VariantProps } from "class-variance-authority";
import { ThemeProvider } from "@/components/theme-provider";
import { Badge, type badgeVariants } from "@/components/ui/badge";
import { Button, type buttonVariants } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Toaster } from "@/components/ui/sonner";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 30_000,
			retry: 1,
		},
	},
});

// cavort-auth's auth endpoints live at the same origin (the Caddy reverse
// proxy fans / out to the backend). Setting the base to an empty string keeps
// requests origin-relative, which is what the same-site cookie auth requires.
setApiBase("");

// cavort-auth's ComponentSlots hands `variant`/`size` over as plain strings
// (the lib does not know which variants a host's Button defines). The shadcn
// Button takes the cva unions, so this adapter narrows with a runtime check
// and falls back to the default variant for a value the host does not have -
// a wrong-but-rendered button beats a type-unsafe cast or a crash.
type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>["variant"]>;
type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>["size"]>;

const BUTTON_VARIANTS = [
	"default",
	"outline",
	"secondary",
	"ghost",
	"destructive",
	"link",
] as const satisfies readonly ButtonVariant[];
const BUTTON_SIZES = [
	"default",
	"xs",
	"sm",
	"lg",
	"icon",
	"icon-xs",
	"icon-sm",
	"icon-lg",
] as const satisfies readonly ButtonSize[];

function isButtonVariant(value: string | undefined): value is ButtonVariant {
	return BUTTON_VARIANTS.some((v) => v === value);
}

function isButtonSize(value: string | undefined): value is ButtonSize {
	return BUTTON_SIZES.some((v) => v === value);
}

function toButtonVariant(value: string | undefined): ButtonVariant {
	return isButtonVariant(value) ? value : "default";
}

function toButtonSize(value: string | undefined): ButtonSize {
	return isButtonSize(value) ? value : "default";
}

const AuthButton: ComponentSlots["Button"] = ({ variant, size, ...props }) => (
	<Button variant={toButtonVariant(variant)} size={toButtonSize(size)} {...props} />
);

// Same narrowing for Badge, whose slot also passes `variant` as a string.
type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;
const BADGE_VARIANTS = [
	"default",
	"secondary",
	"destructive",
	"outline",
	"ghost",
	"link",
] as const satisfies readonly BadgeVariant[];

function isBadgeVariant(value: string | undefined): value is BadgeVariant {
	return BADGE_VARIANTS.some((v) => v === value);
}

function toBadgeVariant(value: string | undefined): BadgeVariant {
	return isBadgeVariant(value) ? value : "default";
}

const AuthBadge: ComponentSlots["Badge"] = ({ variant, ...props }) => (
	<Badge variant={toBadgeVariant(variant)} {...props} />
);

// Tabs aren't in @/components/ui yet — provide minimal stand-ins so the
// ComponentSlots interface is satisfied. cavort-auth components that need
// Tabs (e.g. AdminAuthPanel) will pick these up; if Burresi later adds a
// real Tabs primitive, swap them in.
const Tabs = ({
	children,
	className,
}: {
	defaultValue?: string;
	value?: string;
	onValueChange?: (value: string) => void;
	children?: React.ReactNode;
	className?: string;
}) => <div className={className}>{children}</div>;
const TabsList = (props: React.HTMLAttributes<HTMLDivElement>) => <div {...props} />;
const TabsTrigger = ({
	children,
	value,
	...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { value: string }) => (
	<button type="button" data-tab-value={value} {...rest}>
		{children}
	</button>
);
const TabsContent = ({
	children,
	value,
	...rest
}: React.HTMLAttributes<HTMLDivElement> & { value: string }) => (
	<div data-tab-value={value} {...rest}>
		{children}
	</div>
);

const components: ComponentSlots = {
	Button: AuthButton,
	Card,
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
	CardFooter,
	Input,
	Label,
	Badge: AuthBadge,
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	Tabs,
	TabsList,
	TabsTrigger,
	TabsContent,
	Select,
	SelectTrigger,
	SelectValue,
	SelectContent,
	SelectItem,
	Separator,
};

export const Route = createRootRoute({
	component: RootLayout,
});

function RootLayout() {
	return (
		<ThemeProvider>
			<QueryClientProvider client={queryClient}>
				<CavortAuthProvider
					config={{ apiBase: "", appName: "Burresi" }}
					components={components}
					queryClient={false}
				>
					<Outlet />
					<Toaster />
					<ReactQueryDevtools initialIsOpen={false} />
				</CavortAuthProvider>
			</QueryClientProvider>
		</ThemeProvider>
	);
}
